import React from "react";
import { motion } from "framer-motion";
import { SERVICES } from "@/constants/testIds";
import {
  Search,
  Megaphone,
  Target,
  MousePointerClick,
  Globe,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    slug: "seo",
    title: "Search Engine Optimization",
    short: "SEO",
    desc: "Rank for what your customers actually search. Technical fixes, on-page strategy, content engines, and high-authority link building.",
    icon: Search,
    points: ["Keyword strategy", "Technical SEO audits", "Content pillars", "Link building"],
    hero: true,
  },
  {
    slug: "smm",
    title: "Social Media Marketing",
    short: "SMM",
    desc: "Build a brand people remember. Organic content + paid social across Instagram, LinkedIn and Facebook.",
    icon: Megaphone,
    points: ["Content calendar", "Paid social", "Creator strategy"],
  },
  {
    slug: "leadgen",
    title: "Lead Generation",
    short: "Lead Gen",
    desc: "Predictable inbound — funnels, landing pages and nurture flows engineered for conversion.",
    icon: Target,
    points: ["Funnel design", "Landing pages", "CRM nurture"],
  },
  {
    slug: "ppc",
    title: "Pay-Per-Click Ads",
    short: "PPC",
    desc: "Google Ads, Meta Ads, YouTube — performance campaigns optimized down to the last keyword.",
    icon: MousePointerClick,
    points: ["Google Ads", "Meta Ads", "Retargeting"],
  },
  {
    slug: "web",
    title: "Website Making",
    short: "Websites",
    desc: "Fast, SEO-friendly, conversion-tuned websites — built to sell, not just to look good.",
    icon: Globe,
    points: ["Landing pages", "Full websites", "CRO"],
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative py-24 lg:py-40 bg-[#F4F4F5]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 lg:mb-24">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-block h-2 w-2 bg-zinc-900" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Services
              </span>
            </div>
            <p className="text-sm text-zinc-500">02 / 06</p>
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-zinc-900">
              Five services.
              <br />
              <span className="text-zinc-400">One outcome — growth.</span>
            </h2>
          </div>
        </div>

        {/* Tetris Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-zinc-200 border border-zinc-200">
          {services.map((s, idx) => {
            const Icon = s.icon;
            const span = s.hero
              ? "md:col-span-12 lg:col-span-7 md:row-span-2"
              : idx === 1
              ? "md:col-span-6 lg:col-span-5"
              : idx === 2
              ? "md:col-span-6 lg:col-span-5"
              : idx === 3
              ? "md:col-span-6 lg:col-span-6"
              : "md:col-span-6 lg:col-span-6";
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: idx * 0.06 }}
                data-testid={SERVICES.card(s.slug)}
                className={`group bg-white p-8 lg:p-10 ${span} hover:bg-zinc-900 hover:text-white transition-colors duration-300 cursor-pointer relative`}
              >
                <div className="flex items-start justify-between">
                  <Icon className="h-8 w-8 text-zinc-900 group-hover:text-[#3B5BDC] transition-colors" />
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-400 group-hover:text-zinc-500">
                    0{idx + 1}
                  </span>
                </div>
                <div className="mt-10">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-400 mb-3">
                    {s.short}
                  </p>
                  <h3
                    className={`font-display tracking-tight leading-tight ${
                      s.hero ? "text-4xl lg:text-6xl" : "text-2xl lg:text-3xl"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p className="mt-5 text-base text-zinc-600 group-hover:text-zinc-300 leading-relaxed max-w-lg">
                    {s.desc}
                  </p>
                  {s.points && (
                    <ul className="mt-6 flex flex-wrap gap-2">
                      {s.points.map((p) => (
                        <li
                          key={p}
                          className="text-xs px-3 py-1.5 border border-zinc-300 group-hover:border-zinc-700 text-zinc-700 group-hover:text-zinc-300"
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <ArrowUpRight className="absolute top-8 right-8 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
